import React from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Divider, LinearProgress, Paper, Tab, Tabs } from "@mui/material";

import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import { useEnvConfig } from "hooks/useEnvConfig";
import { BASE_CONSTANTS } from "constants/baseConstants";
import { BaseTabPanel } from "./Base/BaseTabPanel";
import { TabLabel } from "./Base/TabLabel";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const BaseTabsContext = React.createContext(null);

export const useBaseTabsContext = () => {
  const context = React.useContext(BaseTabsContext);

  if (!context) throw new Error("BaseTabsContext not provided!");

  return context;
};

export const BaseTabs = ({
  tabs = [],
  defaultValue = "",
  loading = false,
  variant = "scrollable",
  scrollButtons = "auto",
  disableDivider = false,
  actions = null,
  children,
}) => {
  const { DEV_MODE } = useEnvConfig();

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || defaultValue || tabs[0].value;

  // methods
  const handleOnTabChange = (_, value) =>
    setSearchParams({ ...Object.fromEntries(searchParams), tab: value });

  const filteredTabs = React.useMemo(() => {
    if (DEV_MODE) return tabs;

    return tabs?.filter((tab) => !tab?.inDevelopment);
  }, [DEV_MODE, tabs]);

  if (!tabs?.length) return null;

  return (
    <BaseTabsContext.Provider
      value={{
        tabs: filteredTabs,
        activeTab,
        activeTabDetail: tabs.find((t) => t?.value === activeTab),
      }}
    >
      <Paper elevation={0} className="md:sticky md:top-[62px] md:z-10">
        <Box
          className="relative"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Tabs
            {...{
              variant,
              scrollButtons,
              value: activeTab,
              onChange: handleOnTabChange,
            }}
          >
            {filteredTabs?.map((tab, index) => (
              <Tab
                key={index}
                label={<TabLabel {...{ tab }} />}
                value={tab.value}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>

          {!loading && !!actions && (
            <Box paddingRight={BASE_CONSTANTS.PADDING_SM + 0.5}>{actions}</Box>
          )}
        </Box>

        {loading && (
          <div className="absolute bottom-0 left-0 right-0">
            <LinearProgress />
          </div>
        )}

        {!disableDivider && <Divider />}
      </Paper>

      {(() => {
        if (loading) return <RowsSkeleton />;

        if (children) return children;

        if (tabs?.some((el) => el?.component))
          return tabs?.map((tab, i) => (
            <BaseTabs.Panel key={tab.label + tab.value + i} value={tab?.value}>
              {tab?.component ?? (
                <div className="p-4 text-center">Invalid Tab Component!</div>
              )}
            </BaseTabs.Panel>
          ));
      })()}
    </BaseTabsContext.Provider>
  );
};

BaseTabs.Panel = BaseTabPanel;
